import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, KpiCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS_STYLES, fmtDate } from "@/lib/helpers";
import { LogIn, LogOut, Timer, CalendarOff, Plus, Check, X, Hourglass, FileText, Users, ScanFace } from "lucide-react";
import { toast } from "sonner";
import FaceScanDialog from "@/components/FaceScanDialog";
import { TOKEN_KEY } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (path) => `${API}/api/files/${path}?token=${encodeURIComponent(localStorage.getItem(TOKEN_KEY) || "")}`;

const pad = (n) => String(n).padStart(2, "0");
const localDate = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const localTime = () => { const d = new Date(); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; };

const downloadBlob = async (path, filename) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const EXT_STATUS = {
  Pending: "bg-amber-100 text-amber-800 border-amber-200",
  Approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected: "bg-red-100 text-red-700 border-red-200",
};

export default function Attendance() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = ["founder", "admin"].includes(user?.role);
  const isOperator = !!user?.employee_id;
  const [extOpen, setExtOpen] = useState(false);
  const [regEmp, setRegEmp] = useState("all");
  const [range, setRange] = useState({ start: firstOfMonth(), end: localDate() });
  const [dl, setDl] = useState(false);
  const [extForm, setExtForm] = useState({ date: localDate(), extra_hours: 1, reason: "" });

  const { data: records = [], isLoading } = useQuery({ queryKey: ["attendance"], queryFn: async () => (await api.get("/attendance")).data });
  const { data: today } = useQuery({ queryKey: ["attendance-today"], queryFn: async () => (await api.get(`/attendance/today?date_str=${localDate()}&time_str=${localTime()}`)).data, enabled: isOperator, refetchInterval: 30000 });
  const { data: faceMe } = useQuery({ queryKey: ["face-me"], queryFn: async () => (await api.get("/face/me")).data, enabled: isOperator });
  const { data: faceList = [] } = useQuery({ queryKey: ["face-list"], queryFn: async () => (await api.get("/face/admin/list")).data, enabled: isAdmin });
  const { data: extensions = [] } = useQuery({ queryKey: ["extensions"], queryFn: async () => (await api.get("/extensions")).data });
  const { data: holidayReqs = [] } = useQuery({ queryKey: ["holiday-requests"], queryFn: async () => (await api.get("/holiday-requests")).data });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data, enabled: isAdmin });
  const empName = (id) => employees.find((e) => e.employee_id === id)?.name || id;

  const invalidate = () => ["attendance", "attendance-today", "extensions", "holiday-requests", "executive", "performance", "management"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const punch = useMutation({
    mutationFn: (dir) => api.post(`/attendance/punch-${dir}`, { date: localDate(), time: localTime() }),
    onSuccess: (_, dir) => { invalidate(); toast.success(dir === "in" ? "Punched in — clock running" : "Punched out — break time"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Action failed"),
  });
  const reqExt = useMutation({
    mutationFn: () => api.post("/extensions", { ...extForm, extra_hours: Number(extForm.extra_hours) }),
    onSuccess: () => { invalidate(); toast.success("Extension requested — sent to the Head"); setExtOpen(false); setExtForm({ date: localDate(), extra_hours: 1, reason: "" }); },
    onError: () => toast.error("Request failed"),
  });
  const reviewExt = useMutation({
    mutationFn: ({ id, status }) => api.put(`/extensions/${id}/status`, { status }),
    onSuccess: (_, v) => { invalidate(); toast.success(`Extension ${v.status.toLowerCase()}`); },
  });
  const delExt = useMutation({ mutationFn: (id) => api.delete(`/extensions/${id}`), onSuccess: () => { invalidate(); toast.success("Removed"); } });
  const resetFace = useMutation({
    mutationFn: (empId) => api.delete(`/face/${empId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["face-list"] }); toast.success("Face ID cleared — employee can re-register"); },
    onError: () => toast.error("Could not reset Face ID"),
  });
  const remindFace = useMutation({
    mutationFn: () => api.post("/face/remind"),
    onSuccess: (r) => toast.success(`Reminder emailed to ${r.data.sent} employee(s)${r.data.skipped ? ` · ${r.data.skipped} skipped` : ""}`),
    onError: () => toast.error("Could not send reminders"),
  });
  const approveFace = useMutation({
    mutationFn: (empId) => api.post(`/face/${empId}/approve`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["face-list"] }); toast.success("Face ID approved"); },
    onError: () => toast.error("Could not approve Face ID"),
  });

  const reqHoliday = useMutation({
    mutationFn: () => api.post("/holiday-requests", { date: localDate(), reason: "Request to work on holiday/weekend" }),
    onSuccess: () => { invalidate(); toast.success("Access request sent to the Head/Founder"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Request failed"),
  });
  const reviewHoliday = useMutation({
    mutationFn: ({ id, status }) => api.put(`/holiday-requests/${id}/status`, { status }),
    onSuccess: (_, v) => { invalidate(); toast.success(`Request ${v.status.toLowerCase()}`); },
  });
  const myHolidayReqToday = holidayReqs.find((r) => r.date === localDate());

  const validRange = () => {
    if (!range.start || !range.end) { toast.error("Pick a start and end date"); return false; }
    if (range.start > range.end) { toast.error("Start date must be before end date"); return false; }
    return true;
  };
  const downloadSheet = async (empId, empLabel) => {
    if (!validRange()) return;
    setDl(true);
    try {
      await downloadBlob(`/attendance/pdf?employee_id=${empId}&start=${range.start}&end=${range.end}`,
        `Attendance_${empLabel.replace(/ /g, "_")}_${range.start}_to_${range.end}.pdf`);
      toast.success("Attendance sheet downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    finally { setDl(false); }
  };
  const downloadBulk = async () => {
    if (!validRange()) return;
    setDl(true);
    try {
      await downloadBlob(`/attendance/pdf/bulk?start=${range.start}&end=${range.end}`,
        `Attendance_All_Employees_${range.start}_to_${range.end}.pdf`);
      toast.success("Bulk attendance sheets downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    finally { setDl(false); }
  };

  const cap = today?.cap ?? 4;
  const worked = today?.working_hours ?? 0;

  // ---------------- Face ID (register / verify / periodic re-check) ----------------
  const RECHECK_MS = 90 * 60 * 1000; // every 1.5 hours
  const [face, setFace] = useState({ open: false, mode: "verify", grace: 0 });
  const [lastVerify, setLastVerify] = useState(Date.now());
  const faceRegistered = !!faceMe?.registered;
  const faceStatus = faceMe?.status;
  const facePending = faceRegistered && faceStatus === "pending";
  const storedDesc = faceMe?.descriptors || [];

  const registerFace = () => setFace({
    open: true, mode: "register", grace: 0,
    title: "Register your Face ID",
    description: "Center your face and capture 3 quick angles. Your photo is sent to the Founder for approval before you can punch in.",
    onRegistered: async (descriptors, photo) => {
      try {
        await api.post("/face/register", { descriptors, photo });
        await qc.invalidateQueries({ queryKey: ["face-me"] });
        await qc.invalidateQueries({ queryKey: ["face-list"] });
        toast.success("Face submitted — awaiting Founder approval");
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not save your face"); }
    },
  });

  const startPunch = (dir) => {
    if (!faceRegistered) { registerFace(); return; }
    if (facePending) { toast.error("Your Face ID is awaiting Founder approval. You can punch in once it's approved."); return; }
    setFace({
      open: true, mode: "verify", grace: 0, stored: storedDesc,
      title: dir === "in" ? "Face ID — Punch In" : "Face ID — Punch Out",
      description: "Follow the on-screen prompt (blink or turn your head) to verify it's really you.",
      onVerified: () => { punch.mutate(dir); if (dir === "in") setLastVerify(Date.now()); },
    });
  };

  const autoPunchOut = async (snapshot) => {
    try { await api.post("/attendance/face-fail", { date: localDate(), time: localTime(), snapshot }); } catch { /* ignore */ }
    invalidate();
    toast.error("Face check failed — you've been punched out.");
  };
  const openRecheck = () => setFace({
    open: true, mode: "verify", grace: 120, stored: storedDesc,
    title: "Still working? Quick face check",
    description: "Follow the on-screen prompt (blink or turn) within 2 minutes to stay punched in.",
    onVerified: () => {
      setLastVerify(Date.now());
      api.post("/attendance/face-check", { date: localDate(), time: localTime(), result: "pass" }).catch(() => {});
      toast.success("Verified — keep going");
    },
    onFail: autoPunchOut,
  });

  // Periodic re-check every 1.5h while punched in.
  useEffect(() => {
    if (!today?.punched_in || !faceRegistered) return;
    const id = setInterval(() => {
      if (Date.now() - lastVerify >= RECHECK_MS && !face.open) openRecheck();
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today?.punched_in, faceRegistered, lastVerify, face.open]);

  const displayRecords = isAdmin && regEmp !== "all" ? records.filter((r) => r.employee_id === regEmp) : records;
  const pendingExt = extensions.filter((e) => e.status === "Pending").length;

  // Live "time worked today" — ticks every second while punched in & the tab is visible.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!today?.punched_in) return;
    const id = setInterval(() => { if (document.visibilityState === "visible") setNowTick(Date.now()); }, 1000);
    return () => clearInterval(id);
  }, [today?.punched_in]);

  const openLogin = today?.punched_in ? today.sessions?.[today.sessions.length - 1]?.login : null;
  const openSec = (() => {
    if (!openLogin) return 0;
    const [hh, mm] = openLogin.split(":").map(Number);
    const start = new Date(nowTick); start.setHours(hh, mm, 0, 0);
    return Math.max(0, Math.floor((nowTick - start.getTime()) / 1000));
  })();
  const liveSec = Math.min(Math.round(cap * 3600), Math.round(worked * 3600) + (today?.punched_in ? openSec : 0));
  const fmtHMS = (s) => `${Math.floor(s / 3600)}h ${pad(Math.floor((s % 3600) / 60))}m ${pad(s % 60)}s`;
  const pct = Math.min(100, (liveSec / (cap * 3600)) * 100);

  return (
    <div data-testid="attendance-page">
      <PageHeader title="Attendance Register" subtitle="Punch in & out around your breaks · weekends & company holidays off" />

      {/* ---------------- Operator self-service ---------------- */}
      {isOperator && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <SectionCard title={`Today — ${fmtDate(localDate())}`} className="lg:col-span-2" testId="today-card">
            {!today ? <Skeleton className="h-40" /> : today.is_holiday && !today.has_holiday_access ? (
              <div className="rounded-lg bg-gold/15 p-6" data-testid="holiday-banner">
                <div className="flex items-center gap-4">
                  <div className="rounded-lg bg-navy/10 p-3"><CalendarOff className="h-7 w-7 text-navy" /></div>
                  <div>
                    <p className="font-heading text-lg font-bold text-navy">{today.reason}</p>
                    <p className="text-sm text-slate-600">Attendance isn't required today. Need to work? Request access from the Head/Founder.</p>
                  </div>
                </div>
                <div className="mt-4">
                  {myHolidayReqToday ? (
                    <Badge variant="outline" className={EXT_STATUS[myHolidayReqToday.status]} data-testid="holiday-req-status">
                      Access request: {myHolidayReqToday.status}
                    </Badge>
                  ) : (
                    <Button data-testid="request-holiday-btn" onClick={() => reqHoliday.mutate()} className="bg-navy hover:bg-navy-900 text-white rounded-lg">
                      <Plus className="mr-2 h-4 w-4" /> Request access to work today
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-end justify-between mb-2">
                  <div data-testid="live-timer">
                    <span className="font-heading text-4xl font-extrabold text-navy tabular-nums">
                      {today.punched_in ? fmtHMS(liveSec) : `${worked} hrs`}
                    </span>
                    <span className="text-slate-500"> / {cap} hrs</span>
                  </div>
                  <Badge variant="outline" className={today.punched_in ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}>
                    {today.punched_in ? "● Working now" : "On break / not punched in"}
                  </Badge>
                </div>
                <Progress value={pct} className="h-2.5 mb-4" />
                <div className="flex flex-wrap items-center gap-3">
                  {today.punched_in ? (
                    <Button data-testid="punch-out-btn" onClick={() => startPunch("out")} className="bg-red-600 hover:bg-red-700 text-white rounded-lg">
                      <LogOut className="mr-2 h-4 w-4" /> Punch Out
                    </Button>
                  ) : (
                    <Button data-testid="punch-in-btn" onClick={() => startPunch("in")} disabled={facePending} className="bg-navy hover:bg-navy-900 text-white rounded-lg disabled:opacity-60">
                      {!faceRegistered
                        ? <><ScanFace className="mr-2 h-4 w-4" /> Register Face to Punch In</>
                        : facePending
                          ? <><ScanFace className="mr-2 h-4 w-4" /> Face ID Pending Approval</>
                          : <><LogIn className="mr-2 h-4 w-4" /> Punch In</>}
                    </Button>
                  )}
                  <span className="text-xs text-slate-500">
                    Standard {today.standard_hours} hrs{today.extension_hours > 0 ? ` · +${today.extension_hours} hr extension approved` : ""}
                  </span>
                  {worked >= today.standard_hours && today.extension_hours === 0 && (
                    <Button variant="outline" data-testid="request-extension-btn" onClick={() => setExtOpen(true)} className="rounded-lg border-gold text-gold-dark hover:bg-gold/10">
                      <Plus className="mr-1 h-3.5 w-3.5" /> Request Extension
                    </Button>
                  )}
                  {faceRegistered && (
                    <Button variant="ghost" size="sm" data-testid="reregister-face-btn" onClick={registerFace} className="rounded-lg text-slate-500 hover:text-navy">
                      <ScanFace className="mr-1 h-3.5 w-3.5" /> Re-register Face
                    </Button>
                  )}
                </div>

                {today.sessions?.length > 0 && (
                  <div className="mt-5 overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow>{["Session", "In", "Out", "Hours"].map((h) => <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>)}</TableRow></TableHeader>
                      <TableBody>
                        {today.sessions.map((s, i) => (
                          <TableRow key={i} data-testid={`session-row-${i}`}>
                            <TableCell>#{i + 1}</TableCell>
                            <TableCell>{s.login || "—"}</TableCell>
                            <TableCell>{s.logout || <span className="text-emerald-600">running…</span>}</TableCell>
                            <TableCell>{s.hours || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="My Time Extensions"
            testId="my-extensions-card"
            action={<Button size="sm" variant="outline" onClick={() => setExtOpen(true)} data-testid="ext-add-btn" className="rounded-lg"><Plus className="mr-1 h-3.5 w-3.5" /> Request</Button>}
          >
            {extensions.length === 0 ? <p className="text-slate-400 text-center py-6 text-sm">No extension requests yet.</p> : (
              <div className="space-y-2">
                {extensions.map((e) => (
                  <div key={e.ext_id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid={`ext-${e.ext_id}`}>
                    <div>
                      <div className="text-sm font-semibold text-navy">+{e.extra_hours} hr · {fmtDate(e.date)}</div>
                      <div className="text-xs text-slate-500 max-w-[160px] truncate">{e.reason || "—"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={EXT_STATUS[e.status]}>{e.status}</Badge>
                      {e.status === "Pending" && <Button variant="ghost" size="icon" onClick={() => delExt.mutate(e.ext_id)}><X className="h-4 w-4 text-red-500" /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {/* ---------------- Admin: holiday/weekend work-access approvals ---------------- */}
      {isAdmin && holidayReqs.length > 0 && (
        <SectionCard title="Holiday / Weekend Work Access Requests" className="mb-6" testId="admin-holiday-card">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>{["Employee", "Date", "Reason", "Status", ""].map((h) => <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {holidayReqs.map((r) => (
                  <TableRow key={r.req_id} data-testid={`admin-holiday-${r.req_id}`} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{empName(r.employee_id)}</TableCell>
                    <TableCell>{fmtDate(r.date)}</TableCell>
                    <TableCell className="max-w-[220px] truncate text-slate-500">{r.reason || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={EXT_STATUS[r.status]}>{r.status}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {r.status === "Pending" && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => reviewHoliday.mutate({ id: r.req_id, status: "Approved" })} data-testid={`holiday-approve-${r.req_id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => reviewHoliday.mutate({ id: r.req_id, status: "Rejected" })} data-testid={`holiday-reject-${r.req_id}`}><X className="h-4 w-4 text-red-500" /></Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      )}

      {/* ---------------- Admin: extension approvals ---------------- */}
      {isAdmin && (
        <SectionCard title="Time Extension Requests" className="mb-6" testId="admin-extensions-card">
          {extensions.length === 0 ? <p className="text-slate-400 text-center py-6">No extension requests.</p> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>{["Employee", "Date", "Extra Hrs", "Reason", "Status", ""].map((h) => <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {extensions.map((e) => (
                    <TableRow key={e.ext_id} data-testid={`admin-ext-${e.ext_id}`} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{empName(e.employee_id)}</TableCell>
                      <TableCell>{fmtDate(e.date)}</TableCell>
                      <TableCell>+{e.extra_hours} hr</TableCell>
                      <TableCell className="max-w-[220px] truncate text-slate-500">{e.reason || "—"}</TableCell>
                      <TableCell><Badge variant="outline" className={EXT_STATUS[e.status]}>{e.status}</Badge></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {e.status === "Pending" && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => reviewExt.mutate({ id: e.ext_id, status: "Approved" })} data-testid={`ext-approve-${e.ext_id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => reviewExt.mutate({ id: e.ext_id, status: "Rejected" })} data-testid={`ext-reject-${e.ext_id}`}><X className="h-4 w-4 text-red-500" /></Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </SectionCard>
      )}

      {/* ---------------- Admin: Face ID enrolment & reset ---------------- */}
      {isAdmin && (
        <SectionCard
          title="Face ID Enrolment"
          className="mb-6"
          testId="admin-face-card"
          action={
            <Button
              size="sm" variant="outline" disabled={remindFace.isPending}
              onClick={() => remindFace.mutate()} data-testid="face-remind-btn"
              className="rounded-lg border-gold text-gold-dark hover:bg-gold/10"
            >
              <ScanFace className="mr-1 h-3.5 w-3.5" /> Email reminders
            </Button>
          }
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>{["Photo", "Employee", "Designation", "Face ID", "Enrolled", ""].map((h) => <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {faceList.map((f) => (
                  <TableRow key={f.employee_id} data-testid={`face-row-${f.employee_id}`} className="hover:bg-slate-50">
                    <TableCell>
                      {f.photo_path ? (
                        <a href={fileUrl(f.photo_path)} target="_blank" rel="noreferrer" data-testid={`face-photo-${f.employee_id}`}>
                          <img src={fileUrl(f.photo_path)} alt={f.name} className="h-11 w-11 rounded-lg object-cover ring-1 ring-slate-200 [transform:scaleX(-1)]" />
                        </a>
                      ) : <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-slate-400"><ScanFace className="h-4 w-4" /></div>}
                    </TableCell>
                    <TableCell className="font-medium">{f.name}</TableCell>
                    <TableCell className="text-slate-500">{f.designation || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        !f.registered ? "bg-slate-100 text-slate-600 border-slate-200"
                          : f.status === "pending" ? "bg-amber-100 text-amber-800 border-amber-200"
                            : "bg-emerald-100 text-emerald-800 border-emerald-200"}>
                        {!f.registered ? "Not registered" : f.status === "pending" ? "Pending approval" : "Approved"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500">{f.updated_at ? fmtDate(f.updated_at.slice(0, 10)) : "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        {f.registered && f.status === "pending" && (
                          <Button
                            size="sm" disabled={approveFace.isPending}
                            onClick={() => approveFace.mutate(f.employee_id)}
                            data-testid={`approve-face-${f.employee_id}`}
                            className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <Check className="mr-1 h-3.5 w-3.5" /> Approve
                          </Button>
                        )}
                        <Button
                          variant="outline" size="sm" disabled={!f.registered || resetFace.isPending}
                          onClick={() => resetFace.mutate(f.employee_id)}
                          data-testid={`reset-face-${f.employee_id}`}
                          className="rounded-lg border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40"
                        >
                          <ScanFace className="mr-1 h-3.5 w-3.5" /> Reset
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {faceList.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">No employees.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      )}

      {/* ---------------- Download attendance sheets ---------------- */}
      <SectionCard title="Download Attendance Sheets" testId="attendance-download-card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:flex-wrap">
          <div className="flex-1 min-w-[140px]">
            <Label>From</Label>
            <Input type="date" data-testid="download-start-date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} />
          </div>
          <div className="flex-1 min-w-[140px]">
            <Label>To</Label>
            <Input type="date" data-testid="download-end-date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} />
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin ? (
              <>
                <Button
                  disabled={dl || regEmp === "all"}
                  data-testid="download-individual-btn"
                  onClick={() => downloadSheet(regEmp, empName(regEmp))}
                  className="bg-gold-dark hover:bg-gold text-white rounded-lg"
                  title={regEmp === "all" ? "Select an employee below first" : "Download selected employee's sheet"}
                >
                  <FileText className="mr-2 h-4 w-4" /> Individual PDF
                </Button>
                <Button disabled={dl} data-testid="download-bulk-btn" onClick={downloadBulk} className="bg-navy hover:bg-navy-900 text-white rounded-lg">
                  <Users className="mr-2 h-4 w-4" /> Download All (Bulk)
                </Button>
              </>
            ) : (
              <Button disabled={dl} data-testid="download-my-attendance-btn" onClick={() => downloadSheet(user?.employee_id, user?.name || "Me")} className="bg-gold-dark hover:bg-gold text-white rounded-lg">
                <FileText className="mr-2 h-4 w-4" /> Download My Sheet
              </Button>
            )}
          </div>
        </div>
        {isAdmin && regEmp === "all" && (
          <p className="mt-3 text-xs text-slate-500">Select a specific employee in the register below to enable the individual PDF, or use “Download All” for a combined sheet.</p>
        )}
      </SectionCard>

      {/* ---------------- Attendance register / history ---------------- */}
      <SectionCard
        title={isAdmin ? (regEmp === "all" ? "Attendance Register — All Employees" : `Attendance — ${empName(regEmp)}`) : "My Attendance History"}
        testId="attendance-table-card"
        action={isAdmin && (
          <Select value={regEmp} onValueChange={setRegEmp}>
            <SelectTrigger data-testid="register-employee-select" className="h-9 w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      >
        {isLoading ? <Skeleton className="h-64" /> : displayRecords.length === 0 ? (
          <p className="text-slate-400 text-center py-8">No attendance records yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {[...(isAdmin ? ["Name"] : []), "Date", "First In", "Last Out", "Sessions", "Hours", "Status"].map((h) => <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayRecords.map((r) => (
                  <TableRow key={r.att_id} data-testid={`attendance-row-${r.att_id}`} className="hover:bg-slate-50">
                    {isAdmin && <TableCell className="font-medium">{empName(r.employee_id)}</TableCell>}
                    <TableCell>{fmtDate(r.date)}</TableCell>
                    <TableCell>{r.login_time || "—"}</TableCell>
                    <TableCell>{r.logout_time || "—"}</TableCell>
                    <TableCell>{(r.sessions || []).length || (r.login_time ? 1 : 0)}</TableCell>
                    <TableCell>{r.working_hours || 0}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLES[r.status]}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>

      {/* Request Extension dialog */}
      <Dialog open={extOpen} onOpenChange={setExtOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-navy">Request Time Extension</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Ask the Head for extra working hours beyond your 4-hour part-time schedule.</p>
            <div><Label>Date</Label><Input type="date" data-testid="ext-date-input" value={extForm.date} onChange={(e) => setExtForm({ ...extForm, date: e.target.value })} /></div>
            <div><Label>Extra Hours</Label><Input type="number" min="0.5" step="0.5" data-testid="ext-hours-input" value={extForm.extra_hours} onChange={(e) => setExtForm({ ...extForm, extra_hours: e.target.value })} /></div>
            <div><Label>Reason</Label><Textarea data-testid="ext-reason-input" value={extForm.reason} onChange={(e) => setExtForm({ ...extForm, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={() => reqExt.mutate()} data-testid="ext-submit-btn" className="bg-navy text-white">Send Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <FaceScanDialog
        open={face.open}
        onOpenChange={(o) => setFace((f) => ({ ...f, open: o }))}
        mode={face.mode}
        stored={face.stored || []}
        graceSeconds={face.grace || 0}
        title={face.title}
        description={face.description}
        onRegistered={face.onRegistered}
        onVerified={face.onVerified}
        onFail={face.onFail}
      />
    </div>
  );
}
